import { useState } from "react";
import { PaperAirplaneIcon } from "@heroicons/react/24/outline";
import { toast } from "../../utils/toast";
import { Button, InfoBox, Input, Property, SectionTitle, Select } from "../ui";
import { bursariesApi } from "../../api/hliLists";
import { errorMessageOf } from "../../api/scholarships";
import { TOAST, locationOptions, useChildLocations, useList } from "./shared";
import { useAcademicYears } from "../../hooks/useAcademicYears";

const LOAN_CATEGORY = "Tuition Fees and Living Allowance";

// Keeps a prefilled value selectable even when the list does not contain it
const withCurrent = (options, value) =>
  value && !options.some((o) => o.value === value) ? [{ value, label: value }, ...options] : options;

/* Local loan request built from the claimed bursary record plus family and home location, for the
   record's academic year, while the institution takes requests. The API works out the scores. */
export default function ApplyTab({ bursary, onApplied }) {
  const { record, requestsOpen, request } = bursary;
  const [fatherName, setFatherName] = useState(record.fatherNames || "");
  const [motherName, setMotherName] = useState(record.motherNames || "");
  const [guardianName, setGuardianName] = useState("");
  const [combination, setCombination] = useState(record.combination?.code || "");
  const [completionYear, setCompletionYear] = useState(record.completionYearCode || "");
  const [district, setDistrict] = useState("");
  const [sector, setSector] = useState("");
  const [cell, setCell] = useState("");
  const [village, setVillage] = useState("");
  const [busy, setBusy] = useState(false);

  const districts = useList(["districts"], "location/getDistricts");
  const combinations = useList(["combinations"], "combination/getCombinations");
  const { data: academicYears = [] } = useAcademicYears();
  const sectors = useChildLocations(district);
  const cells = useChildLocations(sector);
  const villages = useChildLocations(cell);

  const combinationOptions = combinations.map((c) => ({ value: c.combinationCode, label: c.combinationName }));
  const yearOptions = academicYears.map((y) => ({ value: y.academicYearCode, label: y.academicYearName }));

  const submit = async (event) => {
    event.preventDefault();
    if (!fatherName.trim() || !motherName.trim() || !combination || !completionYear || !village) {
      toast.warning("Please, provide all necessary information", TOAST);
      return;
    }
    setBusy(true);
    try {
      const { data } = await bursariesApi.request({
        fatherName: fatherName.trim(),
        motherName: motherName.trim(),
        guardianName: guardianName.trim() || null,
        combinationCode: combination,
        completionYear,
        sectorCode: sector,
        cellCode: cell,
        villageCode: village,
      });
      toast.success("Your local loan request is successfully received.", TOAST);
      onApplied(data);
    } catch (error) {
      console.error("Failed to submit the loan request", error);
      toast.error(errorMessageOf(error, "Please, try again"), TOAST);
    } finally {
      setBusy(false);
    }
  };

  if (request) {
    return <InfoBox>Your local loan request is received. Follow it under Loan status.</InfoBox>;
  }
  if (!requestsOpen) {
    return <InfoBox>Your institution is not taking local loan requests now. Please, check again later.</InfoBox>;
  }

  // Changing a location clears everything below it
  const pickDistrict = (code) => {
    setDistrict(code);
    setSector("");
    setCell("");
    setVillage("");
  };
  const pickSector = (code) => {
    setSector(code);
    setCell("");
    setVillage("");
  };
  const pickCell = (code) => {
    setCell(code);
    setVillage("");
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-8">
      <section className="flex flex-col gap-3">
        <SectionTitle title="Your bursary record" subtitle="Taken from your record; contact HEC if anything is wrong" />
        <div className="grid gap-x-10 md:grid-cols-2">
          <Property label="Names" value={[record.firstName, record.lastName].filter(Boolean).join(" ") || undefined} />
          <Property label="National ID" value={record.nationalId} />
          <Property label="Registration number" value={record.registrationNumber} />
          <Property label="Index number" value={record.indexNumber} />
          <Property label="S6 aggregate" value={record.s6Aggregate} />
          <Property label="College" value={record.college?.name} />
          <Property label="Campus" value={record.campusName} />
          <Property label="Qualification" value={record.qualification?.name ?? record.qualification?.code} />
          <Property label="Study area" value={record.qualification?.studyArea} />
          <Property label="Academic year" value={record.academicYear?.name} />
          <Property label="Loan category" value={LOAN_CATEGORY} />
        </div>
      </section>

      <section className="flex flex-col gap-4">
        <SectionTitle title="Studies" />
        <div className="grid gap-5 md:grid-cols-2">
          <Select
            label="Combination"
            required
            placeholder="Select combination"
            value={combination}
            onChange={(e) => setCombination(e.target.value)}
            options={withCurrent(combinationOptions, combination)}
          />
          <Select
            label="Completion year"
            required
            placeholder="Select completion year"
            value={completionYear}
            onChange={(e) => setCompletionYear(e.target.value)}
            options={withCurrent(yearOptions, completionYear)}
          />
        </div>
      </section>

      <section className="flex flex-col gap-4">
        <SectionTitle title="Family" />
        <div className="grid gap-5 md:grid-cols-3">
          <Input label="Father's names" required value={fatherName} onChange={(e) => setFatherName(e.target.value)} />
          <Input label="Mother's names" required value={motherName} onChange={(e) => setMotherName(e.target.value)} />
          <Input label="Guardian" value={guardianName} onChange={(e) => setGuardianName(e.target.value)} placeholder="Optional" />
        </div>
      </section>

      <section className="flex flex-col gap-4">
        <SectionTitle title="Home location" />
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <Select label="District" required placeholder="Select district" value={district} onChange={(e) => pickDistrict(e.target.value)} options={locationOptions(districts)} />
          <Select label="Sector" required placeholder="Select sector" value={sector} onChange={(e) => pickSector(e.target.value)} options={locationOptions(sectors)} disabled={!district} />
          <Select label="Cell" required placeholder="Select cell" value={cell} onChange={(e) => pickCell(e.target.value)} options={locationOptions(cells)} disabled={!sector} />
          <Select label="Village" required placeholder="Select village" value={village} onChange={(e) => setVillage(e.target.value)} options={locationOptions(villages)} disabled={!cell} />
        </div>
      </section>

      <Button type="submit" loading={busy} icon={<PaperAirplaneIcon className="h-4 w-4" />} className="self-start">
        Submit loan request
      </Button>
    </form>
  );
}
